// src/lib/useSequencedRequest.js
//
// Stale-response protection (same contract as the Clinic/Doctor portals).
//
//   * a monotonically increasing SEQUENCE NUMBER: only the latest request may
//     write state, even if an older one resolves later;
//   * an AbortController per run, so a superseded request stops using the
//     connection.
//
// A background refresh keeps the current data on screen; only a foreground
// load with no data yet should show a skeleton in its place.

import { useCallback, useEffect, useRef, useState } from "react";

export function isAbortError(error) {
  return error?.name === "CanceledError" || error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

export default function useSequencedRequest(initialData = null) {
  const [data, setData] = useState(initialData);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const sequenceRef = useRef(0);
  const controllerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const run = useCallback(async (task) => {
    const ticket = sequenceRef.current + 1;
    sequenceRef.current = ticket;

    controllerRef.current?.abort();
    const controller = typeof AbortController === "undefined" ? null : new AbortController();
    controllerRef.current = controller;
    setLoading(true);

    try {
      const result = await task({ signal: controller?.signal });
      if (!mountedRef.current || sequenceRef.current !== ticket) return null;
      setData(result);
      setError(null);
      return result;
    } catch (caught) {
      if (!mountedRef.current || sequenceRef.current !== ticket) return null;
      if (!isAbortError(caught)) setError(caught);
      return null;
    } finally {
      if (mountedRef.current && sequenceRef.current === ticket) setLoading(false);
    }
  }, []);

  const cancel = useCallback(() => {
    sequenceRef.current += 1;
    controllerRef.current?.abort();
    setLoading(false);
  }, []);

  return { data, error, loading, run, cancel, setData, setError };
}
